import React, { useEffect, useState } from 'react';
import './AudioMix.css';

export default function AudioMix() {
  const [audioFiles, setAudioFiles] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [mixUrl, setMixUrl] = useState(null);
  const [mixing, setMixing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAudioFiles();
  }, []);

  async function fetchAudioFiles() {
    try {
      setLoading(true);
      const res = await fetch('/api/audio');
      const data = await res.json();
      if (data.success) {
        setAudioFiles(data.audio.map((f, idx) => ({ ...f, index: idx })));
      }
    } catch (err) {
      console.error('Error fetching audio:', err);
      setError('Failed to load audio files');
    } finally {
      setLoading(false);
    }
  }

  function toggleSelect(name) {
    setSelected((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  function moveFile(index, direction) {
    setAudioFiles((prev) => {
      const items = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= items.length) return items;
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      return items;
    });
  }

  async function handleMix() {
    setMixUrl(null); setMixing(true); setError(''); setSuccess('');
    const formData = new FormData();
    audioFiles.forEach((file, index) => {
      if (selected[file.name]) {
        formData.append(`checkbox_${file.name}`, 'on');
        formData.append(`path_${file.name}`, file.path);
        formData.append(`rank_${file.name}`, index + 1);
      }
    });
    try {
      const res = await fetch('/api/mix', { method: 'POST', body: formData });
      const result = await res.json();
      if (result.success) {
        setSuccess(result.message || 'Mix created successfully!');
        setMixUrl(result.outputUrl);
      } else {
        setError(result.error || 'Mix failed');
      }
    } catch (err) {
      console.error('Error creating mix:', err);
      setError('Error creating mix: ' + err.message);
    } finally {
      setMixing(false);
    }
  }

  async function handleSaveToGithub() {
    setIsSaving(true);
    try {
      const res = await fetch('/save-to-github', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filePath: mixUrl }),
      });
      const result = await res.json();
      if (result.success) {
        setSuccess('Saved to GitHub and database!');
      } else {
        setError('Save failed: ' + result.error);
      }
    } catch (err) {
      console.error(err);
      setError('Error connecting to server');
    } finally {
      setIsSaving(false);
    }
  }

  if (loading) {
    return <div className="mix-loading"><div className="mix-spinner" /></div>;
  }

  const localFiles = audioFiles.filter((f) => f.source === 'local');
  const githubFiles = audioFiles.filter((f) => f.source === 'github');
  const visibleFiles = tab === 'local' ? localFiles : tab === 'github' ? githubFiles : audioFiles;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="mix-view">
      <div className="mix-header">
        <h1>Audio Mix Studio</h1>
        <a href="/record" className="mihu-btn-secondary-lg">Record new audio</a>
      </div>

      {error && <div className="mix-error">{error}</div>}
      {success && <div className="mix-success">{success}</div>}

      <div className="mix-tabs">
        <button className={tab === 'all' ? 'active' : ''} onClick={() => setTab('all')}>All ({audioFiles.length})</button>
        <button className={tab === 'local' ? 'active' : ''} onClick={() => setTab('local')}>Local ({localFiles.length})</button>
        <button className={tab === 'github' ? 'active' : ''} onClick={() => setTab('github')}>GitHub ({githubFiles.length})</button>
      </div>

      {visibleFiles.length === 0 ? (
        <div className="mix-empty">
          <div className="mix-empty-icon">🎵</div>
          <p>No audio files found</p>
          <a href="/record" className="mihu-btn-primary-lg">Record audio</a>
        </div>
      ) : (
        <div className="mix-list">
          {visibleFiles.map((file, i) => (
            <div key={file.name} className={"mix-row" + (selected[file.name] ? " selected" : "")}>
              <div className="mix-row-order">
                <button onClick={() => moveFile(i, -1)} disabled={i === 0} aria-label="Move up">▲</button>
                <span>{i + 1}</span>
                <button onClick={() => moveFile(i, 1)} disabled={i === visibleFiles.length - 1} aria-label="Move down">▼</button>
              </div>
              <span className={"mix-source-chip" + (file.source === 'github' ? ' github' : '')}>
                {file.source === 'github' ? '☁ GitHub' : '📁 Local'}
              </span>
              <div className="mix-row-body">
                <div className="mix-row-name">{file.name}</div>
                <audio controls preload="metadata">
                  <source src={file.path} type="audio/wav" />
                  <source src={file.path} type="audio/mpeg" />
                </audio>
              </div>
              <label className="mix-row-select">
                <input type="checkbox" checked={!!selected[file.name]} onChange={() => toggleSelect(file.name)} />
                Select
              </label>
            </div>
          ))}
        </div>
      )}

      {audioFiles.length > 0 && (
        <div className="mix-controls">
          <p>Select files and use the arrows to reorder. Files mix in the order shown.</p>
          <button className="mihu-btn-primary-lg" onClick={handleMix} disabled={mixing || selectedCount === 0}>
            {mixing ? 'Mixing…' : `Mix ${selectedCount} selected file${selectedCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {mixUrl && (
        <div className="mix-preview">
          <h2>🎧 Mix preview</h2>
          <audio controls src={mixUrl} />
          <div className="mix-preview-actions">
            <a className="mihu-btn-secondary-lg" href={mixUrl} download="mix-output.mp3">Download mix</a>
            <button className="mihu-btn-primary-lg" onClick={handleSaveToGithub} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save to GitHub & list'}
            </button>
            <button className="mix-clear-btn" onClick={() => setMixUrl(null)}>Clear preview</button>
          </div>
        </div>
      )}
    </div>
  );
}
